import { Fragment, useCallback, useEffect, useState } from 'react';
import set from 'lodash/set';
import Accordion from '@nerdwallet/react-accordion';
import { Box, CheckboxOption, Input } from '@nerdwallet/currency';
import { useDispatch } from 'react-redux';
import {
  getInitialState,
  updateComponent,
} from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';

import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import styles from './segment-tracking-form.module.scss';

export type SegmentTrackingProps = {
  includeShoppingFinishId: boolean;
  enableInteractionTracking?: boolean;
  entityName?: string;
  impressionSection?: string;
  impressionPosition?: string;
  location?: string;
  subLocation?: string;
  shoppingFinishId?: string;
};

export const SegmentTrackingForm = ({
  isMarketplaceOfferButtonType = false,
  isCustomUrlButtonType = false,
}: {
  isMarketplaceOfferButtonType?: boolean;
  isCustomUrlButtonType?: boolean;
}) => {
  const [state, setState] = useState<{ segment: SegmentTrackingProps }>({
    segment: {
      entityName: '',
      impressionSection: '',
      impressionPosition: '',
      enableInteractionTracking: false,
      location: '',
      subLocation: '',
      includeShoppingFinishId: false,
      shoppingFinishId: '',
    },
  });
  const dispatch = useDispatch();
  const { componentPath } = useDynamicGrid();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const currentComponent = flatOutline?.[selectedComponentId];

  const handleUpdateComponent = useCallback(
    (field: string, value: string | boolean | object) => {
      set(currentComponent, `props.${componentPath}.${field}`, value);
      dispatch(updateComponent(currentComponent));
    },
    [currentComponent, componentPath, dispatch]
  );
  const handleEntityNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('segment.entityName', e.target.value);
    setState((prev) => ({
      ...prev,
      segment: { ...prev.segment, entityName: e.target.value },
    }));
  };

  const handleEnableInteractionTrackingChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent(
      'segment.enableInteractionTracking',
      e.target.checked
    );
    setState((prev) => ({
      ...prev,
      segment: { ...prev.segment, enableInteractionTracking: e.target.checked },
    }));
  };

  const handleImpressionSectionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('segment.impressionSection', e.target.value);
    setState((prev) => ({
      ...prev,
      segment: { ...prev.segment, impressionSection: e.target.value },
    }));
  };

  const handleImpressionPositionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('segment.impressionPosition', e.target.value);
    setState((prev) => ({
      ...prev,
      segment: { ...prev.segment, impressionPosition: e.target.value },
    }));
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('segment.location', e.target.value);
    setState((prev) => ({
      ...prev,
      segment: { ...prev.segment, location: e.target.value },
    }));
  };

  const handleSubLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('segment.subLocation', e.target.value);
    setState((prev) => ({
      ...prev,
      segment: { ...prev.segment, subLocation: e.target.value },
    }));
  };

  const handleIncludeShoppingFinishIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('segment.includeShoppingFinishId', e.target.checked);
    setState((prev) => ({
      ...prev,
      segment: { ...prev.segment, includeShoppingFinishId: e.target.checked },
    }));
  };

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      segment: {
        entityName: getInitialState(
          currentComponent,
          componentPath,
          'segment.entityName',
          ''
        ),
        impressionSection: getInitialState(
          currentComponent,
          componentPath,
          'segment.impressionSection',
          ''
        ),
        impressionPosition: getInitialState(
          currentComponent,
          componentPath,
          'segment.impressionPosition',
          ''
        ),
        enableInteractionTracking: getInitialState(
          currentComponent,
          componentPath,
          'segment.enableInteractionTracking',
          false
        ),
        location: getInitialState(
          currentComponent,
          componentPath,
          'segment.location',
          ''
        ),
        subLocation: getInitialState(
          currentComponent,
          componentPath,
          'segment.subLocation',
          ''
        ),
        includeShoppingFinishId: getInitialState(
          currentComponent,
          componentPath,
          'segment.includeShoppingFinishId',
          false
        ),
      },
    }));
  }, [currentComponent, componentPath]);

  return (
    <Box className={styles.accordionWrapper}>
      <Accordion
        items={[
          {
            id: 'segment-tracking',
            title: (
              <Box className={styles.accordionHeaderContainer}>
                <span>Segment Tracking</span>
              </Box>
            ),
            children: (
              <Fragment>
                <Box paddingY={2}>
                  <CheckboxOption
                    disabled={isMarketplaceOfferButtonType}
                    label="Enable Segment Tracking?"
                    checked={
                      state.segment.enableInteractionTracking ||
                      isMarketplaceOfferButtonType
                    }
                    onChange={handleEnableInteractionTrackingChange}
                  />
                </Box>
                <Box paddingY={2}>
                  <CheckboxOption
                    label="Include Shopping Finish Id?"
                    checked={state.segment.includeShoppingFinishId || false}
                    onChange={handleIncludeShoppingFinishIdChange}
                  />
                </Box>
                {isCustomUrlButtonType && (
                  <Fragment>
                    <Box paddingY={2}>
                      <Input
                        label="Entity Name"
                        fullWidth
                        value={state.segment.entityName}
                        onChange={handleEntityNameChange}
                      />
                    </Box>
                  </Fragment>
                )}
                <Box paddingY={2}>
                  <Input
                    label="Impression Section"
                    fullWidth
                    value={state.segment.impressionSection}
                    onChange={handleImpressionSectionChange}
                  />
                </Box>
                <Box paddingY={2}>
                  <Input
                    label="Impression Position"
                    fullWidth
                    value={state.segment.impressionPosition}
                    onChange={handleImpressionPositionChange}
                  />
                </Box>
                <Box paddingY={2}>
                  <Input
                    label="Location"
                    fullWidth
                    value={state.segment.location}
                    onChange={handleLocationChange}
                  />
                </Box>
                <Box paddingY={2}>
                  <Input
                    label="Sub Location"
                    fullWidth
                    value={state.segment.subLocation}
                    onChange={handleSubLocationChange}
                  />
                </Box>
              </Fragment>
            ),
          },
        ]}
      />
    </Box>
  );
};
